import React, { useEffect, useState } from 'react';
import { Row, Col } from 'antd';
import { Link } from 'react-router-dom';
import {
    HomeOutlined,
    AppleOutlined,
    ThunderboltOutlined,
    SwapOutlined,
    SettingOutlined,
    CarOutlined,
    LinkOutlined,
    DisconnectOutlined,
    PhoneOutlined,
    CustomerServiceOutlined,
    DownloadOutlined,
    UserAddOutlined,
    CheckCircleOutlined,
    ArrowRightOutlined
} from '@ant-design/icons';
import { useScrollAnimation } from '../../common/hooks';
import { FEATURES, GOOGLE_PLAY_URL, APP_STORE_URL, APP_STORE_AVAILABLE } from '../../common/constants';
import './FeaturesPage.css';

// Icon mapping
const iconMap = {
    'thunderbolt': ThunderboltOutlined,
    'swap': SwapOutlined,
    'setting': SettingOutlined,
    'car': CarOutlined,
    'link': LinkOutlined,
    'disconnect': DisconnectOutlined,
    'phone': PhoneOutlined,
    'customer-service': CustomerServiceOutlined,
};

// Color gradients for icons
const colorGradients = {
    '#fa8c16': 'linear-gradient(135deg, #fa8c16 0%, #ffc53d 100%)',
    '#52c41a': 'linear-gradient(135deg, #52c41a 0%, #95de64 100%)',
    '#1890ff': 'linear-gradient(135deg, #1890ff 0%, #69c0ff 100%)',
    '#13c2c2': 'linear-gradient(135deg, #13c2c2 0%, #5cdbd3 100%)',
    '#722ed1': 'linear-gradient(135deg, #722ed1 0%, #b37feb 100%)',
    '#eb2f96': 'linear-gradient(135deg, #eb2f96 0%, #ff85c0 100%)',
    '#2f54eb': 'linear-gradient(135deg, #2f54eb 0%, #85a5ff 100%)',
    '#faad14': 'linear-gradient(135deg, #D4A853 0%, #f5d89a 100%)',
};

const FeaturesPage = () => {
    const [pageLoaded, setPageLoaded] = useState(false);

    // Animation hooks
    const [featuresRef, featuresVisible] = useScrollAnimation({ threshold: 0.1 });
    const [stepsRef, stepsVisible] = useScrollAnimation({ threshold: 0.1 });
    const [ctaRef, ctaVisible] = useScrollAnimation({ threshold: 0.1 });

    useEffect(() => {
        // Animation d'entrée de page
        setTimeout(() => setPageLoaded(true), 100);
    }, []);

    return (
        <div className="features-page">
            {/* Hero Section - Simple Banner */}
            <section className={`page-banner ${pageLoaded ? 'banner-loaded' : ''}`}>
                <div className="banner-bg-pattern"></div>
                <div className="container">
                    <h1 className={`banner-title ${pageLoaded ? 'animate-in' : ''}`}>
                        Fonctionnalités
                    </h1>
                    <p className={`banner-description ${pageLoaded ? 'animate-in' : ''}`}>
                        Découvrez tous les outils pour optimiser vos revenus
                    </p>
                    <nav className={`breadcrumb ${pageLoaded ? 'animate-in' : ''}`}>
                        <Link to="/"><HomeOutlined /> Accueil</Link>
                        <span className="separator">/</span>
                        <span className="current">Fonctionnalités</span>
                    </nav>
                </div>
            </section>

            {/* Features Grid */}
            <section
                ref={featuresRef}
                className={`features-grid-section ${featuresVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`section-header ${featuresVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}>
                        <span className="section-badge">Services</span>
                        <h2>Nos <span className="text-gold">Fonctionnalités</span></h2>
                        <p>Découvrez tout ce que TIGOON Connect peut faire pour vous</p>
                    </div>
                    <Row gutter={[32, 32]}>
                        {FEATURES.map((feature, index) => {
                            const IconComponent = iconMap[feature.icon] || ThunderboltOutlined;
                            const gradient = colorGradients[feature.color] || colorGradients['#fa8c16'];

                            return (
                                <Col xs={24} sm={12} md={8} key={feature.key}>
                                    <div
                                        className={`feature-card-detailed ${featuresVisible ? 'animate-scale is-visible' : 'animate-scale'}`}
                                        style={{ transitionDelay: `${index * 0.1}s` }}
                                    >
                                        <div
                                            className="feature-icon-large"
                                            style={{ background: gradient }}
                                        >
                                            <IconComponent />
                                        </div>
                                        <h3>{feature.title}</h3>
                                        <p>{feature.description}</p>
                                        <Link to={`/features/${feature.key}`} className="feature-link">
                                            <span>En savoir plus</span>
                                            <ArrowRightOutlined />
                                        </Link>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                </div>
            </section>

            {/* How It Works */}
            <section
                ref={stepsRef}
                className={`how-it-works ${stepsVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`section-header ${stepsVisible ? 'animate-fade-down is-visible' : 'animate-fade-down'}`}>
                        <span className="section-badge">Processus</span>
                        <h2>Comment <span className="text-gold">ça marche</span> ?</h2>
                        <p>Rejoignez TIGOON Connect en 3 étapes simples</p>
                    </div>
                    <Row gutter={[32, 48]} className="steps-grid">
                        <Col xs={24} sm={8}>
                            <div
                                className={`step ${stepsVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}
                                style={{ transitionDelay: '0.1s' }}
                            >
                                <div className="step-icon">
                                    <DownloadOutlined />
                                </div>
                                <div className="step-number">1</div>
                                <h3>Téléchargez</h3>
                                <p>Téléchargez l'application TIGOON Connect depuis votre store</p>
                            </div>
                        </Col>
                        <Col xs={24} sm={8}>
                            <div
                                className={`step ${stepsVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}
                                style={{ transitionDelay: '0.2s' }}
                            >
                                <div className="step-icon">
                                    <UserAddOutlined />
                                </div>
                                <div className="step-number">2</div>
                                <h3>Inscrivez-vous</h3>
                                <p>Créez votre compte avec vos informations de chauffeur</p>
                            </div>
                        </Col>
                        <Col xs={24} sm={8}>
                            <div
                                className={`step ${stepsVisible ? 'animate-fade-up is-visible' : 'animate-fade-up'}`}
                                style={{ transitionDelay: '0.3s' }}
                            >
                                <div className="step-icon">
                                    <CheckCircleOutlined />
                                </div>
                                <div className="step-number">3</div>
                                <h3>Profitez</h3>
                                <p>Accédez à toutes les fonctionnalités et avantages exclusifs</p>
                            </div>
                        </Col>
                    </Row>
                    {/* Connecting line */}
                    <div className={`steps-connector ${stepsVisible ? 'animate-in' : ''}`}></div>
                </div>
            </section>

            {/* CTA Section */}
            <section
                ref={ctaRef}
                className={`cta-section ${ctaVisible ? 'section-visible' : ''}`}
            >
                <div className="container">
                    <div className={`cta-content ${ctaVisible ? 'animate-scale is-visible' : 'animate-scale'}`}>
                        <h2>Prêt à rejoindre <span className="text-gold">TIGOON Connect</span> ?</h2>
                        <p>Téléchargez l'application maintenant et commencez à profiter de tous les avantages</p>
                        <div className="cta-buttons">
                            <a href={GOOGLE_PLAY_URL} target="_blank" rel="noopener noreferrer" className="store-badge">
                                <img
                                    src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                                    alt="Google Play"
                                    height="50"
                                />
                            </a>
                            {APP_STORE_AVAILABLE ? (
                                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="store-badge">
                                    <img
                                        src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg"
                                        alt="App Store"
                                        height="50"
                                    />
                                </a>
                            ) : (
                                <div className="store-badge-coming-soon">
                                    <AppleOutlined />
                                    <span>Bientôt disponible</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default FeaturesPage;
